import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatStamp } from "@/lib/format";
import { Loader2, Zap, CheckCircle2 } from "lucide-react";

/** Nightly 8am pull that leaves dashboards + the month's figures ready to open. */
export default function DataPrewarm() {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/prewarm/status");
      setJob(data.status === "idle" ? null : data);
      return data.status;
    } catch { return "idle"; }
  }, []);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const status = await poll();
      if (!stop && status === "running") setTimeout(tick, 4000);
    };
    tick();
    return () => { stop = true; };
  }, [poll]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/admin/prewarm", {});
      toast.success("Preparing every dashboard in the background");
      const tick = async () => { const s = await poll(); if (s === "running") setTimeout(tick, 4000); };
      tick();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };

  const running = job?.status === "running";
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="data-prewarm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" /> Ready-to-open data
          </p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="data-prewarm-status">
            {running
              ? <>Preparing {job.current || "…"} · <span data-testid="data-prewarm-count">{job.done} of {job.total}</span></>
              : job?.status === "interrupted"
                ? "Last run stopped early — press Prepare now to finish it."
                : job?.finished_at
                  ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Updated as of {formatStamp(job.finished_at)}</span>
                  : "Runs automatically every morning at 8am ET so nobody waits on ShipStation. A refresh only re-checks the last 10 days."}
          </p>
        </div>
        <button onClick={start} disabled={running || starting} data-testid="data-prewarm-btn"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-60 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
          {running || starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Prepare now
        </button>
      </div>
      {(job?.errors || []).length > 0 && (
        <ul className="mt-2 text-xs text-red-700 space-y-0.5" data-testid="data-prewarm-errors">
          {job.errors.map((e) => <li key={e.company_name || e.account_id}>{e.company_name}: {e.error}</li>)}
        </ul>
      )}
    </div>
  );
}
